// Fórmulas de combate (aproximações das fórmulas do Tibia Global, ajustáveis para o Baiak).
import type { ItemType } from './items.js';
import type { VocationId } from './vocations.js';

export type Element = 'physical' | 'fire' | 'earth' | 'energy' | 'ice' | 'holy' | 'death' | 'heal' | 'lifedrain' | 'manadrain' | 'drown';

export interface Attacker {
  vocation: VocationId; level: number; magicLevel: number;
  skills: { fist: number; club: number; sword: number; axe: number; distance: number; shielding: number };
}

export interface AttackPlan {
  min: number; max: number; element: Element; range: number;
  manaCost: number; kind: 'melee' | 'distance' | 'wand' | 'spell';
  /** parte elemental da arma/munição (ex.: Fire Sword "11 Fire"): calculada à parte e sem armadura, como no Global */
  elem?: { element: Element; min: number; max: number };
  /** chance de acerto (%) da arma de distância por distância do alvo (1..7); ausente = sempre acerta */
  hit?: (distance: number) => number;
}

/** Skill usada (e treinada) no corpo a corpo: a do tipo da arma, como no Global; monk usa fist. */
export function meleeSkill(voc: string, weapon: ItemType | undefined, sk: Record<string, number>): 'sword' | 'axe' | 'club' | 'fist' {
  if (voc === 'monk' || weapon?.weaponType === 'fist' || !weapon) return 'fist';
  const wt = weapon?.weaponType;
  if (wt === 'sword' || wt === 'axe' || wt === 'club') return wt;
  return (sk.sword ?? 0) >= (sk.club ?? 0) && (sk.sword ?? 0) >= (sk.axe ?? 0) ? 'sword' : (sk.club ?? 0) >= (sk.axe ?? 0) ? 'club' : 'axe';
}

/** "11 Fire" → { 11, fire } */
export function parseElementAttack(e: string | undefined): { value: number; element: Element } | null {
  const m = /(\d+)\s*([A-Za-z]+)/.exec(e ?? '');
  if (!m) return null;
  const el = ELEMENTS[m[2]!.toLowerCase()];
  return el ? { value: Number(m[1]), element: el } : null;
}

const ELEMENTS: Record<string, Element> = {
  energy: 'energy', fire: 'fire', earth: 'earth', poison: 'earth', ice: 'ice', holy: 'holy', death: 'death', physical: 'physical',
};

/** Intervalo entre ataques básicos (ms), como no Tibia. */
export const ATTACK_INTERVAL = 2000;

function parseDamage(d: string | undefined): [number, number] {
  if (!d) return [8, 18];
  const m = d.match(/(\d+)\s*-\s*(\d+)/);
  if (m) return [Number(m[1]), Number(m[2])];
  const v = Number(d.match(/\d+/)?.[0] ?? 13);
  return [Math.round(v * 0.8), Math.round(v * 1.2)];
}

/** Fator de ataque pelo modo de luta (Canary: Player::getAttackFactor): ofensivo 1, equilibrado 0,75, defensivo 0,5. */
export function attackFactor(stance: string): number { return stance === 'offensive' ? 1 : stance === 'defensive' ? 0.5 : 0.75; }

/** Chance de acerto das armas de distância (Canary: WeaponDistance::useWeapon). */
export function distanceHitChance(skill: number, distance: number, maxHit: number): number {
  const s = (cap: number) => Math.min(skill, cap);
  if (maxHit === 75) return ([0, s(74) + 1, s(28) * 2.4 + 8, s(45) * 1.55 + 6, s(58) * 1.25 + 3, s(74) + 1, s(90) * 0.8 + 3, s(104) * 0.7 + 2][distance] ?? 75);
  if (maxHit === 90) return ([0, s(74) * 1.2 + 1, s(28) * 3.2, s(45) * 2, s(58) * 1.55, s(74) * 1.2 + 1, s(90), s(90)][distance] ?? 90);
  if (maxHit === 100) return ([0, s(73) * 1.35 + 1, s(30) * 3.2 + 4, s(48) * 2.05 + 2, s(65) * 1.5 + 2, s(73) * 1.35 + 1, s(87) * 1.2 - 4, s(90) * 1.1 + 1][distance] ?? 100);
  return maxHit;
}

/**
 * Ataque básico com o equipamento atual, com as fórmulas do Canary (Weapons::getMaxWeaponDamage):
 * corpo a corpo máx = 0,085 × fator × ataque × skill + level/5; distância 0,09 × ...; mín = level/5.
 * O ataque elemental da arma entra no total e o dano é dividido entre físico e elemento na proporção dos ataques.
 * Wand/rod: dano fixo da arma e custo de mana por tiro. Sem arma: soco (ataque 7, fist).
 */
export function planAttack(a: Attacker, weapon: ItemType | undefined, ammo: ItemType | undefined, mana: number, factor = 0.75): AttackPlan {
  const lvl = Math.floor(a.level / 5);
  void mana;
  if (weapon && (weapon.weaponType === 'wand' || weapon.weaponType === 'rod')) {
    const el: Element = a.vocation === 'druid' ? 'earth' : 'energy';
    const [min, max] = parseDamage(weapon.damage);
    return { min, max, element: ELEMENTS[(weapon.damageType ?? el).toLowerCase()] ?? el, range: Math.max(3, weapon.range ?? 3), manaCost: weapon.mana ?? 0, kind: 'wand' };
  }
  const isDistance = weapon && (weapon.weaponType === 'distance' || weapon.weaponType === 'missile');
  if (isDistance) {
    const usesAmmo = !!weapon.ammoType;
    const ammoOk = usesAmmo && ammo && (ammo.ammoType === weapon.ammoType || !ammo.ammoType);
    const ea = usesAmmo ? (ammoOk ? parseElementAttack(ammo!.elementAttack) : null) : parseElementAttack(weapon.elementAttack);
    const atk = usesAmmo ? (ammoOk ? (ammo!.attack ?? 0) + (weapon.attack ?? 0) : 0) : (weapon.attack ?? 0);
    const range = Math.min(7, Math.max(1, weapon.range ?? 5));
    // Canary (WeaponDistance::useWeapon): a chance vem da munição (ou da arma de arremesso): fixa se ela tiver hitChance,
    // senão pela fórmula da distância e da skill com o maxHitChance dela; o arco/besta só SOMA o hitChance dele
    const shot = usesAmmo ? (ammoOk ? ammo! : undefined) : weapon;
    const maxHit = shot?.maxHitChance ?? (usesAmmo ? 90 : 75);
    const bowBonus = usesAmmo ? (weapon.hitChance ?? 0) : 0;
    const hit = (d: number) => (shot?.hitChance ? shot.hitChance : distanceHitChance(a.skills.distance, d, maxHit)) + bowBonus;
    let max = Math.round(0.09 * factor * a.skills.distance * (atk + (ea?.value ?? 0)) + lvl), min = lvl;
    let elem: AttackPlan['elem'];
    if (ea) {   // munição elemental: metade física, metade do elemento (Canary: WeaponDistance::getElementDamage)
      max = Math.round(max / 2); min = Math.floor(min / 2);
      elem = { element: ea.element, min: Math.floor(lvl / 2), max: Math.round((0.09 * factor * a.skills.distance * (ea.value + atk) + lvl) / 2) };
    }
    return { min, max: Math.max(min, max), element: 'physical', range, manaCost: 0, kind: 'distance', hit, ...(elem ? { elem } : {}) };
  }
  // corpo a corpo (sem arma = soco com ataque 7; monk usa fist)
  const skill = a.skills[meleeSkill(a.vocation, weapon, a.skills)];
  const weaponAtk = weapon ? weapon.attack ?? 0 : 7;
  const ea = weapon ? parseElementAttack(weapon.elementAttack) : null;
  const total = weaponAtk + (ea?.value ?? 0);
  const max = total > 0 ? Math.round(0.085 * factor * total * skill + lvl) : 0;
  const prop = total > 0 ? weaponAtk / total : 1;
  return { min: Math.floor(lvl * prop), max: Math.max(Math.floor(lvl * prop), Math.round(max * prop)), element: 'physical', range: 1, manaCost: 0, kind: 'melee',
    ...(ea ? { elem: { element: ea.element, min: Math.floor(lvl * (1 - prop)), max: Math.round(max * (1 - prop)) } } : {}) };
}

export function rollRange(min: number, max: number, rnd = Math.random): number {
  return Math.floor(min + rnd() * (max - min + 1));
}
/** Sorteio do Tibia (normal_random): mais perto do meio da faixa. */
export function rollNormalRange(min: number, max: number, rnd = Math.random): number {
  if (max <= min) return min;
  const u = Math.max(1e-9, rnd()), w = rnd();
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * w);
  return Math.round(Math.min(max, Math.max(min, (min + max) / 2 + z * (max - min) / 6)));
}
/** Redução da armadura (Canary: Creature::blockHit): sorteio entre metade e quase toda a armadura; armadura até 3 tira 1. */
export function armorReduce(armor: number, rnd = Math.random): number {
  if (armor > 3) { const lo = Math.floor(armor / 2), hi = armor - (armor % 2 + 1); return lo + Math.floor(rnd() * (hi - lo + 1)); }
  return armor > 0 ? 1 : 0;
}

/** Dano final num monstro: resistências por elemento, defesa (só ataques de arma, com o bloqueio), armadura contra físico e mitigação. */
export function damageToMonster(raw: number, element: Element, mods: Record<string, number>, armor: number, rnd = Math.random, defense = 0, mitigation = 0): number {
  let d = raw * (mods[element] ?? 1);
  if (defense > 0) d -= Math.floor(defense / 2) + Math.floor(rnd() * (defense - Math.floor(defense / 2) + 1));
  if (element === 'physical' && armor > 0 && d > 0) d -= armorReduce(armor, rnd);
  if (mitigation > 0 && element !== 'lifedrain' && element !== 'manadrain' && d > 0) d -= d * mitigation / 100;
  return Math.max(0, Math.round(d));
}

/** Dano final no jogador: armadura reduz dano físico (a defesa do escudo é calculada à parte, com o bloqueio). */
export function damageToPlayer(raw: number, element: Element, armor: number, shieldDefense: number, rnd = Math.random): number {
  let d = raw;
  if (element === 'physical' && shieldDefense > 0) d -= Math.floor(shieldDefense / 2) + Math.floor(rnd() * (shieldDefense - Math.floor(shieldDefense / 2) + 1));
  if (element === 'physical' && d > 0) d -= armorReduce(armor, rnd);
  return Math.max(0, Math.round(d));
}

/** Cura das poções (Tibia atual). */
/** Poções oficiais: cura (vida e/ou mana), level mínimo e vocations que podem usar (vazio = todas). */
export interface PotionDef { kind: 'health' | 'mana' | 'spirit'; min: number; max: number; mmin?: number; mmax?: number; level?: number; vocs?: string[] }
export const POTIONS: Record<string, PotionDef> = {
  // exigências e valores oficiais (actions/items/potions.lua do Canary)
  'Small Health Potion': { kind: 'health', min: 60, max: 90 },
  'Health Potion': { kind: 'health', min: 125, max: 175 },
  'Strong Health Potion': { kind: 'health', min: 250, max: 350, level: 50, vocs: ['knight', 'paladin', 'monk'] },
  'Great Health Potion': { kind: 'health', min: 425, max: 575, level: 80, vocs: ['knight'] },
  'Ultimate Health Potion': { kind: 'health', min: 650, max: 850, level: 130, vocs: ['knight'] },
  'Supreme Health Potion': { kind: 'health', min: 875, max: 1125, level: 200, vocs: ['knight'] },
  'Mana Potion': { kind: 'mana', min: 75, max: 125 },
  'Strong Mana Potion': { kind: 'mana', min: 115, max: 185, level: 50 },
  'Great Mana Potion': { kind: 'mana', min: 150, max: 250, level: 80, vocs: ['sorcerer', 'druid', 'paladin', 'monk'] },
  'Ultimate Mana Potion': { kind: 'mana', min: 425, max: 575, level: 130, vocs: ['sorcerer', 'druid'] },
  'Great Spirit Potion': { kind: 'spirit', min: 250, max: 350, mmin: 100, mmax: 200, level: 80, vocs: ['paladin', 'monk'] },
  'Ultimate Spirit Potion': { kind: 'spirit', min: 420, max: 580, mmin: 250, mmax: 350, level: 130, vocs: ['paladin', 'monk'] },
};
/** poção pelo nome, sem diferenciar maiúsculas (alguns itens vêm em minúsculas, ex.: "supreme health potion") */
const POTIONS_LC = new Map(Object.entries(POTIONS).map(([k, v]) => [k.toLowerCase(), v]));
export const potionOf = (name: string | undefined): PotionDef | undefined => (name ? POTIONS[name] ?? POTIONS_LC.get(name.toLowerCase()) : undefined);


/** Poção pelo nome (sem diferenciar maiúsculas). */
export function potionByName(name: string): PotionDef | undefined {
  const n = name.toLowerCase();
  for (const [k, v] of Object.entries(POTIONS)) if (k.toLowerCase() === n) return v;
  return undefined;
}
/** A vocation pode usar esta poção? (a loja não vende as que ela não usa) */
/** poções de skill do Global (10 min): quem pode usar */
export const SKILL_POTION_VOCS: Record<string, string[]> = { 'berserk potion': ['knight'], 'mastermind potion': ['sorcerer', 'druid'], 'bullseye potion': ['paladin'] };
export function potionVocOk(name: string, voc: string): boolean {
  const sk = SKILL_POTION_VOCS[name.toLowerCase()];
  if (sk) return sk.includes(voc);
  const p = potionByName(name);
  return !p?.vocs?.length || p.vocs.includes(voc);
}

/** Motivo para não poder usar a poção (ou null). */
export function potionBlock(p: PotionDef, voc: string, level: number): string | null {
  if (p.vocs?.length && !p.vocs.includes(voc)) return 'sua vocation não pode usar essa poção';
  if (p.level && level < p.level) return `essa poção é para level ${p.level} ou mais`;
  return null;
}

/** Melhor poção do tipo para a vocation e o level (usada nas recompensas). */
export function bestPotion(kind: 'health' | 'mana' | 'spirit', voc: string, level: number): string {
  let best: string | null = null;
  for (const [n, p] of Object.entries(POTIONS)) {
    if (p.kind !== kind || potionBlock(p, voc, level)) continue;
    if (!best || (p.level ?? 0) >= (POTIONS[best]!.level ?? 0)) best = n;
  }
  // sem poção de espírito para o level ainda: vida
  return best ?? (kind === 'spirit' ? bestPotion('health', voc, level) : kind === 'mana' ? 'Mana Potion' : 'Health Potion');
}

/** Raridade de equipamento que cai de monstros normais: 90,25% comum, 8,5% raro, 1% épico, 0,25% lendário (épico e lendário pela metade em 14/10). */
/** chance de cada raridade [comum, raro, épico, lendário]. Drops (monstros, bosses, fendas, labirinto): épico e lendário
 *  pela metade desde 14/10 (decisão do Arthur). A Roleta e as bags dela seguem a tabela antiga (roulette). */
export const RARITY_TABLE = { normal: [0.9025, 0.085, 0.01, 0.0025], miniboss: [0.8875, 0.095, 0.015, 0.0025], boss: [0.675, 0.25, 0.06, 0.015], roulette: [0.89, 0.085, 0.02, 0.005] };
export function rollRarity(rnd = Math.random, table: number[] = RARITY_TABLE.normal): 0 | 1 | 2 | 3 {
  const r = rnd();
  if (r < table[3]!) return 3;
  if (r < table[3]! + table[2]!) return 2;
  if (r < table[3]! + table[2]! + table[1]!) return 1;
  return 0;
}

/** Bênçãos do Tibia: 5 comuns + 2 aprimoradas. Cada uma reduz 8% da perda de experiência. */
export const BLESSINGS = [
  'Wisdom of Solitude', 'Spark of the Phoenix', 'Fire of the Suns', 'Spiritual Shielding', 'The Embrace of Tibia',
  'Heart of the Mountain', 'Blood of the Mountain',
] as const;
export const REGULAR_BLESS_MASK = 0b0011111;
export const ALL_BLESS_MASK = 0b1111111;
export const countBits = (n: number) => { let c = 0; for (let i = 0; i < 7; i++) if (n & (1 << i)) c++; return c; };

/** Preço de uma bênção comum (Tibia): 2.000 até o level 30, +200 por level até o 120, depois +75 por level. */
export function blessingPrice(level: number, enhanced = false): number {
  const base = level <= 30 ? 2000 : level <= 120 ? 2000 + 200 * (level - 30) : 20000 + 75 * (level - 120);
  return Math.round(base * (enhanced ? 1.3 : 1));
}

/** Experiência perdida ao morrer (Tibia Global), reduzida pelas bênçãos. */
export function deathExpLoss(level: number, exp: number, blessings: number): number {
  const base = level < 24 ? exp * 0.1 : ((level + 50) / 100) * 50 * (level * level - 5 * level + 8);
  return Math.floor(base * Math.max(0, 1 - 0.08 * countBits(blessings & ALL_BLESS_MASK)));
}

/** Chance de perder itens ao morrer conforme as bênçãos comuns (0 a 5): mochila e cada equipamento. */
export function itemLossChance(regularBlessings: number): { container: number; item: number } {
  const f = [1, 0.7, 0.45, 0.25, 0.1, 0][Math.max(0, Math.min(5, regularBlessings))]!;
  return { container: f, item: 0.1 * f };
}

